import { useLocation, useNavigate } from "react-router-dom";
import { useCreatorBrowsableUrlPrefs } from "../../../../../context/CreatorBrowsableUrlPrefsContext";
import { useCustomDomain } from "../../../../../hooks/useCustomDomain";
import track, {
  TRACKING_TYPES,
} from "../../../../../Utils/SupabaseServices/Track";
import trackTemplate, {
  TEMPLATE_TRACKING_TYPES,
} from "../../../../../Utils/SupabaseServices/TrackTemplate";
import { TEMPLATE_ANALYTICS_EVENTS } from "../../../../../Utils/SupabaseServices/templateAnalyticsEvents";
import trackEmbed, {
  embedAnalyticsPathname,
  TRACKING_EMBED_TYPES,
} from "../../../../../Utils/SupabaseServices/TrackEmbed";
import {
  legacyLibAnalyticsPathname,
  shouldMirrorLegacyLibAnalytics,
} from "../../../../../Utils/analytics/legacyLibAnalyticsPath";
import { isPublicWebsiteTemplateRoute } from "../../../../../Utils/websiteTemplateGuards";
import { resolveTemplatePageKeyFromPathname } from "../../../MasterTemplateEditor/websitePublicTemplatePath";
import {
  buildCollectionViewAllDestination,
  isDashboardPathname,
  resolveDashboardCollectionFrom,
  resolveDashboardCreatorPageSlug,
  toAbsolutePublicUrl,
} from "../../../../../Utils/url/buildCollectionViewAllDestination";
import { useDesign } from "../../../../../context/DesignContext";
import {
  shouldOpenCollectionInNewWindow,
  useOpenCollectionIn,
} from "../../../../../Utils/embed/openCollectionNavigation";
import { navigateWithViewTransitionAndSmoothScroll } from "../../../../../Utils/viewTransitions";
import { requestEmbedIframeHeightRecalcFor } from "../../../../../Utils/HelperFunction/Useiframeheight";
import { carouselTrackIdentity } from "../../../../../Utils/analytics/carouselAnalyticsKey";

const SeeMoreTile = ({
  data,
  type,
  user_id,
  username,
  unique_id,
  component,
  total_count,
  from,
  designTokens,
  creatorPageSlug,
  isEmbedComponent,
}: any) => {
  const location = useLocation();
  const navigate = useNavigate();
  const openCollectionIn = useOpenCollectionIn();
  const browsableUrlPrefs = useCreatorBrowsableUrlPrefs();
  const { customDomain } = useCustomDomain();
  // Fall back to the live DesignContext when no designTokens prop is supplied
  // (e.g. the dashboard Link in Bio editor) so the tile follows the theme.
  const { secondaryColor, secondaryFont, tertiaryColor, forthColor } =
    useDesign();
  const effectiveSecondary = designTokens?.secondaryColor ?? "#000000";
  const effectiveTertiary = designTokens?.tertiary ?? "#e6e7eb";
  const effectiveForth = designTokens?.forthColor ?? "#e6e7eb";
  const effectiveSecondaryFont = designTokens?.secondaryFontFamily ?? "";

  // Website template editor uses its own collection UX; hide this tile there.
  if (from === "website-template") return null;

  const backgroundColor =
    isDashboardPathname(location.pathname) &&
    !location.pathname.includes("dashboard/design")
      ? "#e8e8e8"
      : effectiveForth;

  // Pre-compute the destination URL at render time so the tile can be rendered
  // as a native <a target="_blank"> in embed contexts. Native anchor clicks are
  // respected by all browsers and sandboxed iframes with allow-popups, avoiding
  // the popup-blocker issues that affect window.open() from inside an iframe.
  const effectiveFrom =
    from ||
    resolveDashboardCollectionFrom(location.pathname) ||
    undefined;

  const effectiveCreatorPageSlug =
    creatorPageSlug ??
    component?.creatorPageSlug ??
    resolveDashboardCreatorPageSlug(location.pathname) ??
    null;

  // Fall back to the component's database id when no route slug is available.
  // This covers legacy embed components created before unique_id/route_name
  // were auto-generated. resolvePublicComponentByRouteSlug handles numeric
  // id lookups on the destination page.
  const effectiveSlug =
    unique_id ||
    component?.route_name ||
    component?.unique_id ||
    String(component?.id ?? "");

  const viewAllUrl = buildCollectionViewAllDestination({
    username,
    componentSlug: effectiveSlug,
    from: effectiveFrom,
    location,
    creatorPageSlug: effectiveCreatorPageSlug,
    customDomain,
    browsableUrlPrefs,
    isEmbedComponent,
    component,
  });

  const useNewWindow = shouldOpenCollectionInNewWindow(
    effectiveFrom,
    openCollectionIn,
    location.pathname,
  );

  // Absolute URL is only needed for the new-window path (embed context).
  const absoluteViewAllUrl = useNewWindow
    ? toAbsolutePublicUrl(viewAllUrl, from, isEmbedComponent)
    : viewAllUrl;

  const fireTracking = () => {
    const identity = carouselTrackIdentity(component);
    const viewAllClick = {
      ...identity,
      post_id: `view-all:${identity.component_id || identity.unique_id || "section"}`,
      name: "View All",
      platform: identity.type || "view-all",
    };
    const viewAllPageClickId =
      identity.component_id || identity.unique_id || "";
    const viewAllPageClick = viewAllPageClickId
      ? { ...viewAllClick, id: viewAllPageClickId }
      : null;
    if (from == "embed-search") {
      const embedPath = embedAnalyticsPathname(username);
      trackEmbed("Carousel Click", TRACKING_EMBED_TYPES.EVENT, {
        pathname: embedPath,
        details: identity,
        user_id: user_id,
        title: "Stay Up To Date",
      });
      trackEmbed("Post Click", TRACKING_EMBED_TYPES.EVENT, {
        pathname: embedPath,
        details: viewAllClick,
        user_id: user_id,
        title: "Stay Up To Date",
      });
      trackEmbed(embedPath, TRACKING_EMBED_TYPES.PAGE, {
        pathname: embedPath,
        details: identity,
        user_id: user_id,
        title: "Stay Up To Date",
      });
    } else {
      const path = location?.pathname || "";
      const pageKey =
        resolveTemplatePageKeyFromPathname(path) || "linkInBio";
      const libPath = legacyLibAnalyticsPathname(username);
      if (isPublicWebsiteTemplateRoute(path)) {
        void trackTemplate(
          TEMPLATE_ANALYTICS_EVENTS.CAROUSEL_TAB,
          TEMPLATE_TRACKING_TYPES.EVENT,
          {
            pathname: path,
            details: identity,
            user_id,
            username,
            page_key: pageKey,
            entity_type: "carousel_tab",
            entity_label: component?.name,
            entity_id: identity.component_id || identity.unique_id,
            carousel_slug: identity.component_id || identity.unique_id,
          },
        );
        if (shouldMirrorLegacyLibAnalytics(path, pageKey)) {
          track("Carousel Click", TRACKING_TYPES.EVENT, {
            pathname: libPath,
            details: viewAllClick,
            user_id: user_id,
            title: "Stay Up To Date",
          });
          if (viewAllPageClick) {
            track("Post Click", TRACKING_TYPES.EVENT, {
              pathname: libPath,
              details: viewAllPageClick,
              user_id: user_id,
              title: "Stay Up To Date",
            });
          }
        }
        // Skip legacy PAGE on public website — template page views cover sessions.
      } else {
        track("Carousel Click", TRACKING_TYPES.EVENT, {
          pathname: libPath,
          details: viewAllClick,
          user_id: user_id,
          title: "Stay Up To Date",
        });
        if (viewAllPageClick) {
          track("Post Click", TRACKING_TYPES.EVENT, {
            pathname: libPath,
            details: viewAllPageClick,
            user_id: user_id,
            title: "Stay Up To Date",
          });
        }
        track(libPath, TRACKING_TYPES.PAGE, {
          pathname: libPath,
          details: identity,
          user_id: user_id,
          title: "Stay Up To Date",
        });
      }
    }
  };

  const handleEmbedClick = () => {
    fireTracking();
    // Also notify any legacy parent-page embed scripts that listen for SB_VIEW_ALL.
    // The actual navigation is handled by the <a target="_blank"> below.
    if (typeof window !== "undefined" && window.parent !== window) {
      try {
        window.parent.postMessage(
          { type: "SB_VIEW_ALL", url: absoluteViewAllUrl },
          "*",
        );
      } catch (_) {}
    }
  };

  const handleInPageClick = () => {
    fireTracking();
    sessionStorage.setItem("componentData", JSON.stringify(data));
    navigateWithViewTransitionAndSmoothScroll(() => {
      navigate(viewAllUrl, { state: { data: data, from: effectiveFrom } });
    }, window);
    requestEmbedIframeHeightRecalcFor(effectiveFrom);
  };

  const isSquare = component?.content_shape === "sq";
  const tileClassName = [
    "creator-feed-image-wrapper-links",
    "tpl-see-more-tile__card",
    isSquare ? "square-wrapper" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const tileStyle = {
    backgroundColor: backgroundColor,
    color: effectiveSecondary,
    display: "flex" as const,
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    height: isSquare
      ? "var(--content-square-size)"
      : "var(--content-rectangle-height)",
    width: isSquare
      ? "var(--content-square-size)"
      : "var(--content-rectangle-width)",
    minHeight: isSquare
      ? "var(--content-square-size)"
      : "var(--content-rectangle-height)",
    borderRadius: "8px",
    fontFamily: effectiveSecondaryFont,
  };

  const tileContent = (
    <>
      <div
        className="tpl-see-more-tile__label"
        style={{
          fontSize: "16px",
          fontWeight: "bold",
          color: tileStyle?.color,
        }}
      >
        View All
      </div>
      <div
        className="tpl-see-more-tile__count"
        style={{
          fontSize: "16px",
          marginBottom: "8px",
          color: tileStyle?.color,
        }}
      >
        {total_count - 25} more items
      </div>
    </>
  );

  if (!data || total_count <= 25) return null;

  return (
    <div className="link-url">
      {useNewWindow ? (
        // In embed/new-window contexts, use a native anchor tag with target="_blank".
        // This is universally respected by browsers and sandboxed iframes with
        // allow-popups — unlike window.open() which popup blockers often suppress
        // when called from inside a cross-origin iframe.
        <a
          href={absoluteViewAllUrl || undefined}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleEmbedClick}
          style={{ textDecoration: "none", color: "inherit", display: "block" }}
        >
          <div className={tileClassName} style={tileStyle}>
            {tileContent}
          </div>
        </a>
      ) : (
        <div
          className={tileClassName}
          style={tileStyle}
          onClick={handleInPageClick}
        >
          {tileContent}
        </div>
      )}
    </div>
  );
};

export default SeeMoreTile;
